/**
 * TransmitControl — the Transmit entry on results and download (FR-016,
 * C-013; TX.4, X.10, GEN.6; style guide §7.1; mission 6 WP03 T023;
 * DM-01M3C8MP7AV3AWFNA6R365X21T).
 *
 * Reads `transmitGate(run)`. Absent (not ready, Path B, or not deliverable) renders
 * nothing: a later-mission control is absent, never disabled. Otherwise a
 * secondary link to the transmit route in both states (owner decision
 * 2026-09-26: Transmit stays secondary in both gate states); with ERROR
 * findings it is relabelled with the gate, "Transmit (acknowledge ERRORs)"
 * with no count (§7.1 as amended, sheet 33 callout 10; owner decision
 * 2026-09-26), and the reason line "N ERROR findings" carries the count.
 */
import { useId } from 'react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import type { Run } from '@/lib/api/runs'
import { gateButton, useBillingGate } from '@/lib/billing-gate'
import { transmitGate } from '@/lib/gates'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'

export interface TransmitControlProps {
  run: Pick<Run, 'run_id' | 'state' | 'path' | 'deliverable' | 'error_count'>
}

export function TransmitControl({ run }: TransmitControlProps) {
  const reasonId = useId()
  const billing = useBillingGate()
  const gate = transmitGate(run)
  if (gate.kind === 'absent') return null
  const to = `/app/runs/${encodeURIComponent(run.run_id)}/transmit`
  if (gate.kind === 'ready') {
    if (billing.refused) {
      return (
        <Button variant="secondary" {...gateButton(billing)}>
          Transmit
        </Button>
      )
    }
    return (
      <Button asChild variant="secondary">
        <Link to={to}>Transmit</Link>
      </Button>
    )
  }
  // owner decision 2026-09-26: without the count the label fits the 260 px rail at 1280 px (NFR-008, SC-009), so
  // it keeps the Button base's nowrap and h-9 (§7.1: buttons never wrap); lane-c cebbaf7's wrap override is gone.
  return (
    <div className="flex flex-col items-start gap-1">
      {billing.refused ? (
        <Button variant="secondary" aria-describedby={reasonId} {...gateButton(billing)}>
          {copyText('transmitAcknowledge')}
        </Button>
      ) : (
        <Button asChild variant="secondary">
          {/* mission 7 WP14: J3, the label and the reason line are register entries (P22). */}
          <Link to={to} aria-describedby={reasonId}>
            {copyText('transmitAcknowledge')}
          </Link>
        </Button>
      )}
      <p id={reasonId} className="text-body-sm text-navy-600">
        {/* mission 6 WP13: handoff from WP03: §7.1's reason in the singular at 1, never "1 ERROR findings". */}
        {PROVISIONAL_COPY.transmitAckReason.render({ count: gate.errors })}
      </p>
    </div>
  )
}
